// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState } from 'react'
import { FileSignature, ChevronLeft, FileText, Trash2, Link2, ShieldCheck, AlertTriangle } from 'lucide-react'
import * as api from '../../api/client'
import EsignFieldEditor from './EsignFieldEditor'
import EsignEmailSettingsCard from './EsignEmailSettingsCard'
import { usePharmacy } from '../../auth/PharmacyContext'


/**
 * CareRx e-signature configuration.
 *
 * D-CRX-73 review (Mario 2026-09-02): "the esign in the pharmacy setting should be on the documents
 * we do generate like consent and enrollment — disable uploading for now."
 *
 * So the page LEADS with the two generated documents and what actually happens when each is sent.
 * Upload is gone — and gone at the endpoint, not just here: the button was never the control. What
 * remains below is the existing template list, kept READ-ONLY-plus-delete so an operator can see and
 * remove a leftover file that is still standing in front of a generated document.
 */
export default function EsignConfigPage() {
  const { mode } = usePharmacy()
  const isPlatformAdmin = mode !== 'pharmacy'   // SaaS operator owns platform-default templates
  const [templates, setTemplates] = useState<api.EsignTemplate[]>([])
  const [editing, setEditing] = useState<api.EsignTemplate | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [bindingFor, setBindingFor] = useState<api.EsignTemplate | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [genDocs, setGenDocs] = useState<api.EsignGeneratedDoc[]>([])

  useEffect(() => { void load() }, [])
  async function load() {
    setLoading(true); setErr(null)
    try {
      const [t, g] = await Promise.all([api.listEsignTemplates(), api.listEsignGeneratedDocs()])
      setTemplates(t.templates)
      setGenDocs(g.documents)
    }
    catch (e: any) { setErr(e?.message || 'Failed to load templates') }
    finally { setLoading(false) }
  }

  async function onDelete(t: api.EsignTemplate) {
    if (!window.confirm(`Delete "${t.name}"? This removes the PDF and its field configuration. This cannot be undone.`)) return
    setDeletingId(t.id); setErr(null)
    try { await api.deleteEsignTemplate(t.id); await load() }
    catch (e: any) { setErr(e?.message || 'Failed to delete template') }
    finally { setDeletingId(null) }
  }

  if (editing) {
    return (
      <div style={{ maxWidth: 'var(--page-max, 1100px)', margin: '0 auto', padding: '8px 0' }}>
        <button onClick={() => { setEditing(null); void load() }} style={backBtn}>
          <ChevronLeft size={16} /> Back to templates
        </button>
        <h1 style={h1}><FileSignature size={20} /> {editing.name}</h1>
        <p style={sub}>Place signature, date, and other fields by selecting a field type and clicking on the document.</p>
        <EsignFieldEditor templateId={editing.id} />
      </div>
    )
  }

  return (
    <div style={{ maxWidth: 'var(--page-max, 820px)', margin: '0 auto', padding: '8px 0' }}>
      <h1 style={h1}><FileSignature size={20} /> E-signature configuration</h1>
      <p style={sub}>Signing runs on the documents OwlRx generates for each therapy — they already carry this pharmacy's branding, address and licensing state, so there is nothing to upload and nothing to re-upload for a second pharmacy.</p>

      {!isPlatformAdmin && <DocusignConnectionPanel onError={setErr} />}
      {!isPlatformAdmin && <EsignEmailSettingsCard />}

      {err && <div style={errBox}>{err}</div>}

      {/* The generated documents lead the page — they are what actually gets signed. Every sentence
          below is composed HERE from the facts the endpoint reports (generator / sends / tabs /
          template_role / blockers); the server returns no prose. That split is not tidiness: while
          the copy lived server-side, the consent's wording was reused for enrollment and told
          operators to delete the only template that makes enrollment signing work. */}
      {!loading && genDocs.map(d => (
        <div key={d.kind} style={genCard}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {d.esign_ready && d.sends === 'generated'
              ? <ShieldCheck size={17} style={{ color: '#15803d', flexShrink: 0 }} />
              : <AlertTriangle size={17} style={{ color: '#b45309', flexShrink: 0 }} />}
            <span style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{d.label}</span>
            <span style={{ ...badge, ...(d.sends === 'generated' ? badgeGenerated : badgeTemplate) }}>
              {d.sends === 'generated' ? 'sends generated' : d.sends === 'template' ? 'sends uploaded file' : 'cannot send'}
            </span>
          </div>

          <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5 }}>
            {d.generator === 'server'
              ? 'Generated per therapy from this pharmacy\u2019s branding, address and licensing state.'
              : 'Generated per therapy in your browser, from the enrollment form spec \u2014 viewing and printing work everywhere.'}
          </div>

          <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.5 }}>
            {d.tabs === 'anchor'
              ? 'Signature, initial and date fields follow anchors the renderer emits, so they move with the document instead of sitting at a fixed page coordinate.'
              : d.tabs === 'coordinate'
                ? 'Signature fields are placed at fixed page coordinates, which only line up with the exact file they were measured on.'
                : 'No document can be sent for signature on this lane yet.'}
          </div>

          {/* An OVERRIDE can be removed; a REQUIRED template is the document itself. */}
          {d.template && d.template_role === 'override' && (
            <div style={warnBox}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                <b>“{d.template.name}”</b> is sent instead of the generated document. Uploaded files
                carry whoever’s letterhead and whichever state’s wording they were made with — delete it
                below and this lane goes back to the generated {d.label.toLowerCase()}.
              </span>
            </div>
          )}
          {d.template && d.template_role === 'required' && (
            <div style={warnBox}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                <b>“{d.template.name}”</b> is not an override — it <b>is</b> the document this lane signs,
                because the generated form is built in the browser and the send path has no server-side
                copy to hand DocuSign. <b>Do not delete it:</b> there is nothing to fall back to, and with
                uploads disabled it cannot be replaced.
              </span>
            </div>
          )}

          {d.blockers.map(code => (
            <div key={code} style={warnBox}>
              <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{
                code === 'docusign_not_configured'
                  ? 'DocuSign is not connected for this pharmacy, so nothing can be sent for signature yet \u2014 connect it above.'
                  : code === 'no_template'
                    ? 'This lane has no document to send. The generated form exists only in the browser, and uploads are disabled, so sending an enrollment form for signature will fail until it gains a server-side renderer.'
                    : code
              }</span>
            </div>
          ))}
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '12px 14px', marginTop: 4,
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 12.5, color: '#64748b', lineHeight: 1.5 }}>
        <FileText size={15} style={{ flexShrink: 0, marginTop: 1, color: '#94a3b8' }} />
        <span><b style={{ color: '#475569' }}>Uploading a template is turned off.</b> The list below is what this
          pharmacy already had. Existing templates can still be opened, re-configured and deleted — nothing was
          removed for you.</span>
      </div>

      {loading ? (
        <div style={loadingBox}>Loading…</div>
      ) : templates.length === 0 ? (
        <div style={emptyBox}>No uploaded templates — signing runs on the generated documents above.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
          {templates.map(t => (
            <div key={t.id} style={{ display: 'flex', flexDirection: 'column' }}>
              <div style={row}>
                <button onClick={() => setEditing(t)} style={rowOpen} title="Configure fields">
                  <FileText size={18} style={{ color: '#0891b2', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={rowName}>{t.name}</div>
                    <div style={rowMeta}>
                      {t.page_count} page{t.page_count === 1 ? '' : 's'} · {t.file_name}
                      {t.attach_mode && t.attach_mode !== 'manual' && t.doc_kind
                        ? ` · ${t.attach_mode === 'replace' ? 'Replaces' : 'Added to'} ${labelKind(t.doc_kind)}`
                        : ' · Not attached'}
                    </div>
                  </div>
                  <span style={{ ...badge, ...(t.scope === 'platform' ? badgePlatform : badgePharmacy) }}>{t.scope}</span>
                  <span style={{ ...statusPill, ...(t.status === 'active' ? statusActive : statusDraft) }}>{t.status}</span>
                </button>
                {(t.scope !== 'platform' || isPlatformAdmin) && (
                  <>
                    <button onClick={() => setBindingFor(bindingFor?.id === t.id ? null : t)} style={attachBtn}
                            title="Attach to a workflow" aria-label={`Attach ${t.name} to a workflow`}>
                      <Link2 size={16} />
                    </button>
                    <button onClick={() => onDelete(t)} disabled={deletingId === t.id} style={deleteBtn}
                            title="Delete this template" aria-label={`Delete ${t.name}`}>
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
              {bindingFor?.id === t.id && (
                <BindingPanel template={t} onSaved={() => { setBindingFor(null); void load() }} onCancel={() => setBindingFor(null)} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const h1: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 9, fontSize: 22, fontWeight: 800, color: '#0f172a', margin: 0 }
const sub: React.CSSProperties = { fontSize: 14, color: '#64748b', margin: '6px 0 20px', lineHeight: 1.5 }
const errBox: React.CSSProperties = { background: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', padding: 12, borderRadius: 10, marginBottom: 16, fontSize: 13 }
const genCard: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 7, padding: '14px 16px', marginBottom: 10, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }
const warnBox: React.CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 7, padding: '9px 11px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12.5, lineHeight: 1.5 }
const badgeGenerated: React.CSSProperties = { background: '#dcfce7', color: '#15803d' }
const badgeTemplate: React.CSSProperties = { background: '#fef3c7', color: '#a16207' }
const loadingBox: React.CSSProperties = { padding: 40, textAlign: 'center', color: '#64748b' }
const emptyBox: React.CSSProperties = { padding: 32, textAlign: 'center', color: '#94a3b8', fontSize: 14, background: '#f8fafc', borderRadius: 12, marginTop: 16 }
const row: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }
const attachBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: 8, border: 'none', background: 'transparent', color: '#0891b2', cursor: 'pointer', flexShrink: 0 }
const rowOpen: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0, padding: '14px 16px', background: 'transparent', border: 'none', cursor: 'pointer' }
const deleteBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, marginRight: 8, borderRadius: 8, border: 'none', background: 'transparent', color: '#dc2626', cursor: 'pointer', flexShrink: 0 }
const rowName: React.CSSProperties = { fontSize: 14, fontWeight: 600, color: '#1e293b' }
const rowMeta: React.CSSProperties = { fontSize: 12, color: '#94a3b8', marginTop: 1 }
const badge: React.CSSProperties = { fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase' }
const badgePlatform: React.CSSProperties = { background: '#ede9fe', color: '#6d28d9' }
const badgePharmacy: React.CSSProperties = { background: '#cffafe', color: '#0e7490' }
const statusPill: React.CSSProperties = { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6 }
const statusActive: React.CSSProperties = { background: '#dcfce7', color: '#15803d' }
const statusDraft: React.CSSProperties = { background: '#fef3c7', color: '#a16207' }
const backBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, fontWeight: 600, color: '#0e7490', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0', marginBottom: 8 }

// ── Workflow attachment vocabulary (mirrors backend ValidSubjectTypes/ValidDocKinds) ───────────
const DOC_KINDS: { kind: string; subject: string; label: string }[] = [
  { kind: 'plan_of_care', subject: 'assessment', label: 'Plan of Care (CareRx / PCW)' },
  { kind: 'enrollment_form', subject: 'enrollment', label: 'Enrollment form' },
  { kind: 'invoice', subject: 'invoice', label: 'Invoice' },
  { kind: 'loa', subject: 'loa', label: 'Letter of authorization' },
]
function labelKind(kind: string): string {
  return DOC_KINDS.find(d => d.kind === kind)?.label ?? kind
}

// Inline panel to attach a template to a workflow document. "Replace" makes this template the sent
// document; "Separate" sends it as an extra signable form; "Manual" leaves it unattached.
function BindingPanel({ template, onSaved, onCancel }: {
  template: api.EsignTemplate; onSaved: () => void; onCancel: () => void
}) {
  const [docKind, setDocKind] = useState<string>(template.doc_kind ?? '')
  const [mode, setMode] = useState<string>(template.attach_mode && template.attach_mode !== 'manual' ? template.attach_mode : 'separate')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function apply(detach: boolean) {
    setSaving(true); setErr(null)
    try {
      if (detach) {
        await api.setEsignBinding(template.id, { subject_type: null, doc_kind: null, attach_mode: 'manual' })
      } else {
        const dk = DOC_KINDS.find(d => d.kind === docKind)
        if (!dk) { setErr('Choose a document to attach to.'); setSaving(false); return }
        await api.setEsignBinding(template.id, { subject_type: dk.subject, doc_kind: dk.kind, attach_mode: mode, activate: true })
      }
      onSaved()
    } catch (e: any) {
      setErr(e?.message || 'Could not update attachment')
    } finally { setSaving(false) }
  }

  return (
    <div style={bindPanel}>
      <div style={{ fontWeight: 600, fontSize: 13, color: '#334155' }}>Attach “{template.name}” to a workflow</div>
      <label style={bindLabel}>Document
        <select value={docKind} onChange={(e) => setDocKind(e.target.value)} style={bindSelect}>
          <option value="">— choose —</option>
          {DOC_KINDS.map(d => <option key={d.kind} value={d.kind}>{d.label}</option>)}
        </select>
      </label>
      <label style={bindLabel}>How
        <select value={mode} onChange={(e) => setMode(e.target.value)} style={bindSelect}>
          <option value="separate">Add as a separate signable form</option>
          <option value="replace">Replace the auto-generated document</option>
        </select>
      </label>
      {err && <div style={{ color: '#b91c1c', fontSize: 12 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => apply(false)} disabled={saving} style={bindSave}>{saving ? 'Saving…' : 'Attach'}</button>
        {template.attach_mode && template.attach_mode !== 'manual' && (
          <button onClick={() => apply(true)} disabled={saving} style={bindDetach}>Detach</button>
        )}
        <button onClick={onCancel} disabled={saving} style={bindCancel}>Cancel</button>
      </div>
    </div>
  )
}

const bindPanel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8, padding: 14, margin: '2px 0 4px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10 }
const bindLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: '#64748b' }
const bindSelect: React.CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: 13, color: '#334155', background: '#fff' }
const bindSave: React.CSSProperties = { padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0891b2', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer' }
const bindDetach: React.CSSProperties = { padding: '8px 14px', borderRadius: 8, border: '1px solid #fecaca', background: '#fff', color: '#b91c1c', fontWeight: 600, fontSize: 13, cursor: 'pointer' }
const bindCancel: React.CSSProperties = { padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontWeight: 600, fontSize: 13, cursor: 'pointer' }

// --- DocuSign API connection (I-977). Sending gates only on a connected config + the Enable
//     sending toggle; a BAA is the pharmacy's own compliance obligation, not an app gate. The
//     secret is write-only (never returned). ---
function DocusignConnectionPanel({ onError }: { onError: (e: string) => void }) {
  const [cfg, setCfg] = useState<api.DocusignConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [form, setForm] = useState({ account_id: '', base_uri: '', integration_key: '', impersonated_user_id: '', secret_kv_ref: '', connect_hmac_kv_ref: '', enabled: false })

  useEffect(() => { void load() }, [])
  async function load() {
    setLoading(true)
    try {
      const c = await api.getMyDocusignConfig()
      setCfg(c)
      setForm({
        account_id: c.account_id ?? '', base_uri: c.base_uri ?? '', integration_key: c.integration_key ?? '',
        impersonated_user_id: c.impersonated_user_id ?? '', secret_kv_ref: '',
        connect_hmac_kv_ref: c.connect_hmac_kv_ref ?? '', enabled: c.enabled,
      })
    } catch (e: any) { onError(e?.message || 'Failed to load DocuSign config') }
    finally { setLoading(false) }
  }
  async function save() {
    setBusy(true); setMsg(null)
    try {
      await api.putMyDocusignConfig({
        account_id: form.account_id.trim() || null, base_uri: form.base_uri.trim() || null,
        integration_key: form.integration_key.trim() || null, impersonated_user_id: form.impersonated_user_id.trim() || null,
        secret_kv_ref: form.secret_kv_ref.trim() || null,
        connect_hmac_kv_ref: form.connect_hmac_kv_ref.trim() || null, enabled: form.enabled,
      })
      setMsg('Saved.'); setEditing(false); await load()
    } catch (e: any) { onError(e?.message || 'Failed to save DocuSign config') }
    finally { setBusy(false) }
  }

  if (loading) return null
  const box: React.CSSProperties = { border: '1px solid var(--color-border,#e2e8f0)', borderRadius: 8, padding: 14, marginBottom: 16, background: 'var(--color-surface,#fff)' }
  const lbl: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-soft,#64748b)', display: 'block', marginBottom: 3 }
  const inp: React.CSSProperties = { width: '100%', padding: '7px 9px', border: '1px solid var(--color-border,#cbd5e1)', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }

  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: 14 }}>DocuSign connection</strong>
        {!editing
          ? <button onClick={() => setEditing(true)} style={{ ...btnGhost }}>Edit</button>
          : <button onClick={save} disabled={busy} style={{ ...btnGhost }}>{busy ? 'Saving…' : 'Save'}</button>}
      </div>
      <div style={{ background: 'var(--color-surface-soft,#f8fafc)', border: '1px solid var(--color-border,#e2e8f0)', borderRadius: 4, padding: 9, fontSize: 12,
                    color: 'var(--color-text-soft,#64748b)', marginBottom: 10, lineHeight: 1.5 }}>
        These settings connect your DocuSign account. Sending is controlled by the <strong>Enable sending</strong>
        toggle below. Ensuring a Business Associate Agreement covers your DocuSign use is your pharmacy&rsquo;s responsibility.
        <br />
        <strong>Connect HMAC KV ref</strong> names the Key Vault secret holding your DocuSign Connect
        signing key — the key itself is never stored here. Set it in DocuSign under Settings →
        Connect → your configuration → <em>HMAC signing</em>, store the key in Key Vault, then enter that
        secret&rsquo;s name. Until it is set, incoming DocuSign status callbacks are rejected and
        signatures only update when staff press <strong>Check signature</strong>.
      </div>
      {msg && <div style={{ fontSize: 12.5, color: '#15803d', marginBottom: 8 }}>{msg}</div>}
      {!editing ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, fontSize: 13 }}>
          <div><span style={lbl}>Account ID</span>{cfg?.account_id || '—'}</div>
          <div><span style={lbl}>Base URI</span>{cfg?.base_uri || '—'}</div>
          <div><span style={lbl}>Integration key</span>{cfg?.integration_key || '—'}</div>
          <div><span style={lbl}>Impersonated user</span>{cfg?.impersonated_user_id || '—'}</div>
          <div><span style={lbl}>Secret</span>{cfg?.has_secret ? 'On file (Key Vault)' : 'Not set'}</div>
          <div><span style={lbl}>Connect HMAC key</span>{cfg?.connect_hmac_kv_ref || '— not set'}</div>
          <div><span style={lbl}>Webhook</span>{cfg?.webhook_verified
            ? 'Signature-verified'
            : 'Not verified — status updates need “Check signature”'}</div>
          <div><span style={lbl}>Sending</span>{cfg?.enabled ? 'Enabled' : 'Disabled'}</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10 }}>
          <div><span style={lbl}>Account ID</span><input style={inp} value={form.account_id} onChange={e => setForm({ ...form, account_id: e.target.value })} /></div>
          <div><span style={lbl}>Base URI</span><input style={inp} placeholder="https://na4.docusign.net" value={form.base_uri} onChange={e => setForm({ ...form, base_uri: e.target.value })} /></div>
          <div><span style={lbl}>Integration key</span><input style={inp} value={form.integration_key} onChange={e => setForm({ ...form, integration_key: e.target.value })} /></div>
          <div><span style={lbl}>Impersonated user ID</span><input style={inp} value={form.impersonated_user_id} onChange={e => setForm({ ...form, impersonated_user_id: e.target.value })} /></div>
          <div><span style={lbl}>Secret KV ref {cfg?.has_secret ? '(leave blank to keep)' : ''}</span><input style={inp} type="password" placeholder={cfg?.has_secret ? '········' : 'key-vault-secret-name'} value={form.secret_kv_ref} onChange={e => setForm({ ...form, secret_kv_ref: e.target.value })} /></div>
          {/* Connect HMAC: the Key Vault secret NAME, not the key. Each pharmacy's DocuSign account
              has its own Connect key, so this is per-pharmacy and cannot be a platform value.
              Blank = inbound webhooks are not verified and fail closed (503). */}
          <div><span style={lbl}>Connect HMAC KV ref</span><input style={inp} placeholder="e.g. DOCUSIGN_CONNECT_HMAC_APPLE" value={form.connect_hmac_kv_ref} onChange={e => setForm({ ...form, connect_hmac_kv_ref: e.target.value })} /></div>
          <div><span style={lbl}>Sending</span>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, paddingTop: 6 }}>
              <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })} /> Enable sending
            </label>
          </div>
        </div>
      )}
    </div>
  )
}

const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', fontSize: 13,
  border: '1px solid var(--color-border,#cbd5e1)', borderRadius: 6, background: 'var(--color-surface,#fff)', cursor: 'pointer',
}
